import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { ArrowLeft, Info, Loader2, ChevronLeft, ChevronRight, Save } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';
import { FAScores, Assessment } from '../types';
import { assessmentService } from '../services/assessmentService';
import { teamService } from '../services/teamService';
import { api } from '../services/api';
import { SKILLS_CATEGORIES } from '../config/skillsCategories';
import { ScoringGuideModal } from '../components/ScoringGuideModal';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { CountdownConfirmModal } from '../components/CountdownConfirmModal';

// ─── FA Model definitions ────────────────────────────────────────────────────

interface AttrDef { key: string; label: string }
interface SectionDef { title: string; color: string; icon: string; attributes: AttrDef[] }

const fmt = (s: string) => s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

const OUTFIELD_SECTIONS: Record<string, SectionDef> = {
  technical: {
    title: SKILLS_CATEGORIES.technical.title,
    color: SKILLS_CATEGORIES.technical.color,
    icon: SKILLS_CATEGORIES.technical.icon,
    attributes: ['ball_control','passing_accuracy','positioning','tactical_awareness','shooting','dribbling'].map(k => ({ key: k, label: fmt(k) })),
  },
  physical: {
    title: SKILLS_CATEGORIES.physical.title,
    color: SKILLS_CATEGORIES.physical.color,
    icon: SKILLS_CATEGORIES.physical.icon,
    attributes: ['speed','endurance','strength','agility','coordination','balance'].map(k => ({ key: k, label: fmt(k) })),
  },
  psychological: {
    title: SKILLS_CATEGORIES.psychological.title,
    color: SKILLS_CATEGORIES.psychological.color,
    icon: SKILLS_CATEGORIES.psychological.icon,
    attributes: ['confidence','resilience','decision_making','concentration','motivation','composure'].map(k => ({ key: k, label: fmt(k) })),
  },
  social: {
    title: SKILLS_CATEGORIES.social.title,
    color: SKILLS_CATEGORIES.social.color,
    icon: SKILLS_CATEGORIES.social.icon,
    attributes: ['teamwork','communication','respect','leadership','coachability','sportsmanship'].map(k => ({ key: k, label: fmt(k) })),
  },
};

const GK_SECTIONS: Record<string, SectionDef> = {
  ...OUTFIELD_SECTIONS,
  technical: {
    title: 'Technical (Goalkeeper)',
    color: SKILLS_CATEGORIES.technical.color,
    icon: SKILLS_CATEGORIES.technical.icon,
    attributes: ['shot_stopping','handling','distribution','positioning','aerial_ability','one_on_one','command_of_area','tactical_awareness'].map(k => ({ key: k, label: fmt(k) })),
  },
};

const defaultScores = (gk: boolean): FAScores => {
  const base = { speed: 5, endurance: 5, strength: 5, agility: 5, coordination: 5, balance: 5 };
  const psych = { confidence: 5, resilience: 5, decision_making: 5, concentration: 5, motivation: 5, composure: 5 };
  const social = { teamwork: 5, communication: 5, respect: 5, leadership: 5, coachability: 5, sportsmanship: 5 };
  const tech = gk
    ? { shot_stopping: 5, handling: 5, distribution: 5, positioning: 5, aerial_ability: 5, one_on_one: 5, command_of_area: 5, tactical_awareness: 5 }
    : { ball_control: 5, passing_accuracy: 5, positioning: 5, tactical_awareness: 5, shooting: 5, dribbling: 5 };
  return { technical: tech, physical: base, psychological: psych, social } as FAScores;
};

// ─── Slider component (Figma-matching) ───────────────────────────────────────

interface SliderProps {
  value: number;
  onChange: (v: number) => void;
  previousValue?: number;
  skillKey: string;
}

function AssessmentSlider({ value, onChange, previousValue, skillKey }: SliderProps) {
  const [guideOpen, setGuideOpen] = useState(false);
  const change = previousValue !== undefined ? Math.round((value - previousValue) * 100) / 100 : undefined;
  const pct = ((value - 1) / 9) * 100;
  const prevPct = previousValue !== undefined ? ((previousValue - 1) / 9) * 100 : undefined;

  const displayValue = Number.isInteger(value) ? value.toString() : value.toFixed(2).replace(/0$/, '');

  return (
    <div className="py-5 border-b border-gray-100 last:border-b-0">
      {/* Header row: skill name + info on left, score + change on right */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className="text-base font-medium text-gray-900">{fmt(skillKey)}</span>
          <button
            type="button"
            onClick={() => setGuideOpen(true)}
            className="w-5 h-5 rounded-full border border-gray-300 text-gray-400 hover:text-gray-600 hover:border-gray-400 flex items-center justify-center transition-colors"
            aria-label={`Scoring guide for ${fmt(skillKey)}`}
          >
            <Info className="w-3 h-3" />
          </button>
        </div>
        <div className="text-right">
          <span className="text-2xl font-bold text-red-500">{displayValue}</span>
          {change !== undefined && change !== 0 && (
            <p className={`text-xs font-medium ${change > 0 ? 'text-green-600' : 'text-red-500'}`}>
              {change > 0 ? '+' : ''}{Number.isInteger(change) ? change : change.toFixed(2).replace(/0$/, '')} from last
            </p>
          )}
        </div>
      </div>

      {/* Tick marks — vertical lines with numbers below, matching Figma */}
      <div className="relative h-10 mx-3">
        {Array.from({ length: 10 }, (_, i) => i + 1).map(n => {
          const pos = ((n - 1) / 9) * 100;
          const isSelected = Math.abs(value - n) < 0.01;
          const isNearest = Math.round(value) === n && !isSelected;
          return (
            <button
              key={n}
              type="button"
              onClick={() => onChange(n)}
              className="absolute flex flex-col items-center"
              style={{ left: `${pos}%`, transform: 'translateX(-50%)', top: 0 }}
              aria-label={`Set score to ${n}`}
            >
              {/* Vertical tick line */}
              <div
                className={`w-px transition-colors ${
                  isSelected ? 'bg-blue-600' : 'bg-gray-300'
                }`}
                style={{ height: isSelected ? 16 : 12 }}
              />
              {/* Number label */}
              <span
                className={`text-xs mt-0.5 transition-colors ${
                  isSelected
                    ? 'text-blue-600 font-bold'
                    : isNearest
                      ? 'text-gray-600 font-medium'
                      : 'text-gray-400'
                }`}
              >
                {n}
              </span>
            </button>
          );
        })}
        {/* Quarter/half sub-ticks between whole numbers */}
        {Array.from({ length: 9 }, (_, i) => i + 1).map(n => {
          const gapStart = ((n - 1) / 9) * 100;
          const gapEnd = (n / 9) * 100;
          const gapWidth = gapEnd - gapStart;
          return [0.25, 0.5, 0.75].map(frac => {
            const tickVal = n + frac;
            const tickPct = gapStart + gapWidth * frac;
            const isHalf = frac === 0.5;
            const isActive = Math.abs(value - tickVal) < 0.01;
            return (
              <button
                key={tickVal}
                type="button"
                onClick={() => onChange(tickVal)}
                className="absolute flex flex-col items-center"
                style={{ left: `${tickPct}%`, transform: 'translateX(-50%)', top: 0 }}
                aria-label={`Set score to ${tickVal}`}
              >
                <div
                  className={`w-px transition-colors ${
                    isActive ? 'bg-blue-600' : isHalf ? 'bg-gray-300' : 'bg-gray-200'
                  }`}
                  style={{ height: isActive ? 14 : isHalf ? 8 : 5 }}
                />
              </button>
            );
          });
        })}
      </div>

      {/* Slider track — solid blue/gray like Figma, not gradient */}
      <div className="relative h-10 flex items-center mx-3">
        {/* Gray background track */}
        <div className="absolute inset-x-0 h-1.5 rounded-full bg-gray-200" />
        {/* Blue filled portion up to thumb */}
        <div
          className="absolute h-1.5 rounded-full bg-blue-600"
          style={{ left: 0, width: `${pct}%` }}
        />
        {/* Previous value ghost thumb */}
        {prevPct !== undefined && (
          <div
            className="absolute w-7 h-7 rounded-full border-2 border-dashed border-gray-300 bg-white/70 pointer-events-none"
            style={{ left: `calc(${prevPct}% - 14px)`, top: '50%', transform: 'translateY(-50%)' }}
          />
        )}
        {/* Current thumb — solid blue circle */}
        <div
          className="absolute w-7 h-7 rounded-full bg-blue-600 border-2 border-white shadow-md pointer-events-none"
          style={{ left: `calc(${pct}% - 14px)`, top: '50%', transform: 'translateY(-50%)' }}
        />
        {/* Invisible range input — 0.25 steps */}
        <input
          type="range"
          min={1}
          max={10}
          step={0.25}
          value={value}
          onChange={e => onChange(parseFloat(e.target.value))}
          className="absolute inset-x-0 h-10 opacity-0 cursor-pointer"
          style={{ margin: 0 }}
        />
      </div>

      {/* Scale labels */}
      <div className="flex justify-between mx-3 mt-1">
        <span className="text-xs text-gray-400">Beginner</span>
        <span className="text-xs text-gray-400">Competent</span>
        <span className="text-xs text-gray-400">Advanced</span>
      </div>

      <ScoringGuideModal skillKey={skillKey} isOpen={guideOpen} onClose={() => setGuideOpen(false)} />
    </div>
  );
}

// ─── Progress bar ────────────────────────────────────────────────────────────

const SECTION_COLORS = ['#ef4444', '#eab308', '#10b981', '#3b82f6'];

// Light background tints per section key
const SECTION_BG: Record<string, string> = {
  technical: 'rgba(239, 68, 68, 0.05)',
  physical: 'rgba(234, 179, 8, 0.05)',
  psychological: 'rgba(16, 185, 129, 0.05)',
  social: 'rgba(59, 130, 246, 0.05)',
};
const SECTION_BORDER: Record<string, string> = {
  technical: 'rgba(239, 68, 68, 0.2)',
  physical: 'rgba(234, 179, 8, 0.2)',
  psychological: 'rgba(16, 185, 129, 0.2)',
  social: 'rgba(59, 130, 246, 0.2)',
};

function ProgressBar({ current, total }: { current: number; total: number }) {
  return (
    <div className="flex items-center mb-6">
      {Array.from({ length: total }, (_, i) => (
        <React.Fragment key={i}>
          {/* Dot */}
          <div
            className="w-3 h-3 rounded-full flex-shrink-0"
            style={{ backgroundColor: i <= current ? SECTION_COLORS[i] : '#e5e7eb' }}
          />
          {/* Connecting bar */}
          {i < total - 1 && (
            <div
              className="flex-1 h-1"
              style={{ backgroundColor: i < current ? SECTION_COLORS[i + 1] : '#e5e7eb' }}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

// ─── Main page ───────────────────────────────────────────────────────────────

type FormData = { scores: FAScores; notes?: string; coachSummary?: string };

export function AssessPlayerPage() {
  const { playerId } = useParams<{ playerId: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [currentSection, setCurrentSection] = useState(0);
  const [showSubmitCountdown, setShowSubmitCountdown] = useState(false);
  const [playerName, setPlayerName] = useState('');
  const [isGoalkeeper, setIsGoalkeeper] = useState(false);
  const [previousAssessment, setPreviousAssessment] = useState<Assessment | null>(null);
  const [draftAssessment, setDraftAssessment] = useState<Assessment | null>(null);

  const sections = isGoalkeeper ? GK_SECTIONS : OUTFIELD_SECTIONS;
  const sectionKeys = Object.keys(sections);
  const currentKey = sectionKeys[currentSection];
  const currentDef = sections[currentKey];

  const { control, handleSubmit, getValues, reset, setValue } = useForm<FormData>({
    defaultValues: { scores: defaultScores(false), notes: '', coachSummary: '' },
  });

  // Load player + assessment data
  useEffect(() => {
    if (!playerId) return;
    (async () => {
      try {
        setLoading(true);
        // Find the player across all teams
        const teamsRes = await teamService.getTeams();
        let foundPlayer: any = null;
        for (const team of teamsRes) {
          try {
            const players = await teamService.getTeamPlayers(team.id);
            const match = players.find((p: any) => p.id === playerId);
            if (match) { foundPlayer = { ...match, teamId: team.id }; break; }
          } catch { /* skip */ }
        }
        if (!foundPlayer) { setError('Player not found'); setLoading(false); return; }

        setPlayerName(`${foundPlayer.firstName} ${foundPlayer.lastName}`);
        const gk = foundPlayer.position?.toLowerCase() === 'goalkeeper';
        setIsGoalkeeper(gk);

        // Load previous assessments
        try {
          const assessments = await assessmentService.getPlayerAssessments(playerId);
          const completed = assessments.filter(a => a.isComplete);
          if (completed.length > 0) setPreviousAssessment(completed[0]);
          const draft = assessments.find(a => !a.isComplete);
          if (draft) {
            setDraftAssessment(draft);
            reset({ scores: draft.scores, notes: draft.notes || '' });
          } else if (completed.length > 0) {
            // Pre-fill with last completed assessment scores
            reset({ scores: completed[0].scores, notes: '' });
          } else {
            reset({ scores: defaultScores(gk), notes: '' });
          }
        } catch {
          reset({ scores: defaultScores(gk), notes: '' });
        }
      } catch (err: any) {
        setError(err.message || 'Failed to load player data');
      } finally {
        setLoading(false);
      }
    })();
  }, [playerId]); // eslint-disable-line react-hooks/exhaustive-deps

  const assessmentType = previousAssessment ? 'FOLLOW_UP' : 'INITIAL';
  const assessmentLabel = draftAssessment ? 'Resume Assessment' : previousAssessment ? 'Follow-Up Assessment' : 'Initial Assessment';

  const saveDraft = useCallback(async () => {
    if (!playerId) return;
    setSaving(true);
    setError('');
    try {
      const data = getValues();
      if (draftAssessment) {
        await assessmentService.updateDraft(draftAssessment.id, { scores: data.scores, notes: data.notes });
      } else {
        const newDraft = await assessmentService.saveDraft({ playerId, type: assessmentType, scores: data.scores, notes: data.notes });
        setDraftAssessment(newDraft);
      }
      navigate(`/players/${playerId}`);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to save draft');
    } finally {
      setSaving(false);
    }
  }, [playerId, draftAssessment, assessmentType, getValues, navigate]);

  const totalSteps = sectionKeys.length + 1; // +1 for summary step
  const isSummaryStep = currentSection === sectionKeys.length;

  const generateSummary = (scores: FAScores, name: string): string => {
    const corners = Object.entries(sections) as [string, SectionDef][];
    const strengths: { name: string; score: number; corner: string }[] = [];
    const focus: { name: string; score: number; corner: string }[] = [];

    corners.forEach(([key, def]) => {
      const cornerScores = scores[key as keyof FAScores] as Record<string, number>;
      if (!cornerScores) return;
      let best = { name: '', score: 0 };
      let worst = { name: '', score: 10 };
      Object.entries(cornerScores).forEach(([attr, score]) => {
        if (score > best.score) best = { name: fmt(attr), score };
        if (score < worst.score) worst = { name: fmt(attr), score };
      });
      if (best.name) strengths.push({ ...best, corner: def.title });
      if (worst.name) focus.push({ ...worst, corner: def.title });
    });

    const topStrengths = strengths.sort((a, b) => b.score - a.score).slice(0, 3);
    const topFocus = focus.sort((a, b) => a.score - b.score).slice(0, 2);

    let summary = `${name} continues to demonstrate strong development across multiple areas. `;
    summary += `Key strengths include ${topStrengths.map(s => `${s.name} (${s.score}/10)`).join(', ')}. `;
    summary += `These are excellent foundations to build upon.\n\n`;
    summary += `To further enhance overall performance, ${name} should focus on developing ${topFocus.map(f => f.name).join(' and ')}. `;
    summary += `With targeted practice in these areas, combined with maintaining current strengths, ${name} has significant potential for continued growth.\n\n`;
    summary += `Overall, ${name} shows consistent effort and a positive attitude towards training. Keep up the excellent work and continue to challenge yourself in all aspects of the game.`;

    return summary;
  };

  const onSubmit = async (data: FormData) => {
    if (!playerId) return;
    // Prevent accidental submission — must be on the summary step
    if (!isSummaryStep) return;
    setSaving(true);
    setError('');
    try {
      const notesWithSummary = data.coachSummary ? `[COACH_SUMMARY]\n${data.coachSummary}\n[/COACH_SUMMARY]${data.notes ? `\n\n${data.notes}` : ''}` : data.notes;
      if (draftAssessment) {
        await assessmentService.completeDraft(draftAssessment.id, { scores: data.scores, notes: notesWithSummary });
      } else {
        await assessmentService.createAssessment({ playerId, type: assessmentType, scores: data.scores as FAScores, notes: notesWithSummary, isComplete: true });
      }

      // Fire-and-forget: trigger automatic report email if enabled
      const autoSend = localStorage.getItem('gamepath_auto_report') !== 'false';
      if (autoSend && playerId) {
        api.post('/reports/send-assessment-email', { playerId }).catch(() => {
          // Non-blocking — don't show errors to user
        });
      }

      // If in onboarding flow, redirect to onboarding complete
      const onboarding = localStorage.getItem('gamepath_onboarding');
      if (onboarding) {
        navigate('/onboarding/complete');
      } else {
        navigate(`/players/${playerId}`);
      }
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to save assessment');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  const isOnboarding = !!localStorage.getItem('gamepath_onboarding');

  return (
    <div className="p-4 lg:p-8 max-w-2xl mx-auto">
      {/* Back link — hidden during onboarding */}
      {!isOnboarding && (
        <Link
          to={playerId ? `/players/${playerId}` : '/players'}
          className="inline-flex items-center gap-1.5 text-sm text-green-700 hover:text-green-800 font-medium mb-4 min-h-[44px]"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Player
        </Link>
      )}

      {/* Title */}
      <h1 className="text-2xl font-bold text-gray-900 mb-1">
        {assessmentLabel} - {playerName}
      </h1>
      <p className="text-sm text-gray-500 mb-4">
        Section {currentSection + 1} of {totalSteps}
      </p>

      {/* Progress bar */}
      <ProgressBar current={currentSection} total={totalSteps} />

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Section card — scored sections */}
      {!isSummaryStep && (
      <div>
        <Card
          className="shadow-sm mb-6"
          style={{
            backgroundColor: SECTION_BG[currentKey] || undefined,
            borderColor: SECTION_BORDER[currentKey] || undefined,
            borderWidth: 1,
          }}
        >
          <CardContent className="p-6">
            {/* Section header */}
            <div className="flex items-center gap-3 mb-6">
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-lg flex-shrink-0"
                style={{ backgroundColor: currentDef.color }}
              >
                {currentDef.icon}
              </div>
              <h2 className="text-xl font-semibold text-gray-900">{currentDef.title}</h2>
            </div>

            {/* Attributes */}
            {currentDef.attributes.map(attr => (
              <Controller
                key={attr.key}
                name={`scores.${currentKey}.${attr.key}` as any}
                control={control}
                render={({ field }) => (
                  <AssessmentSlider
                    value={field.value || 5}
                    onChange={field.onChange}
                    skillKey={attr.key}
                    previousValue={(previousAssessment?.scores?.[currentKey as keyof FAScores] as any)?.[attr.key]}
                  />
                )}
              />
            ))}

          </CardContent>
        </Card>
      </div>
      )}

      {/* Summary step */}
      {isSummaryStep && (
      <div>
        <Card className="shadow-sm mb-6">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-green-600 flex items-center justify-center text-white font-bold text-lg flex-shrink-0">✓</div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Assessment Summary</h2>
                <p className="text-sm text-gray-500">Review and customise the assessment summary</p>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
              <p className="text-xs text-amber-800">
                <span className="font-semibold">💡 Tip:</span> This summary has been automatically generated based on the assessment scores. Feel free to edit and personalise it to better reflect your observations.
              </p>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 mb-1.5 block">Player Summary</label>
              <Controller
                name="coachSummary"
                control={control}
                render={({ field }) => (
                  <textarea
                    {...field}
                    rows={10}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 resize-y leading-relaxed"
                  />
                )}
              />
            </div>

            <p className="text-xs text-gray-400 mt-2">This summary will appear on the player's profile page and in the assessment PDF report.</p>
          </CardContent>
        </Card>
      </div>
      )}

      {/* Navigation buttons */}
      <div className="flex items-center justify-between gap-3 mb-8">
          <div>
            {currentSection > 0 && (
              <Button
                type="button"
                variant="outline"
                onClick={() => { setCurrentSection(s => s - 1); window.scrollTo(0, 0); }}
                className="min-h-[44px]"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Previous
              </Button>
            )}
          </div>

          <div className="flex items-center gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={saveDraft}
              disabled={saving}
              className="min-h-[44px]"
            >
              <Save className="w-4 h-4 mr-1" />
              Save Draft
            </Button>

            {currentSection < sectionKeys.length ? (
              <Button
                type="button"
                onClick={() => {
                  // When moving to summary step, auto-generate the summary
                  if (currentSection === sectionKeys.length - 1) {
                    const data = getValues();
                    const existing = data.coachSummary;
                    if (!existing) {
                      const summary = generateSummary(data.scores, playerName.split(' ')[0] || 'Player');
                      setValue('coachSummary', summary);
                    }
                  }
                  setCurrentSection(s => s + 1);
                  window.scrollTo(0, 0);
                }}
                className="bg-green-600 hover:bg-green-700 text-white min-h-[44px]"
              >
                {currentSection === sectionKeys.length - 1 ? 'Review Summary' : 'Next Section'}
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button
                type="button"
                disabled={saving}
                onClick={() => setShowSubmitCountdown(true)}
                className="bg-green-600 hover:bg-green-700 text-white min-h-[44px]"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Complete Assessment'
                )}
              </Button>
            )}
          </div>
        </div>

      {/* Countdown confirmation modal */}
      <CountdownConfirmModal
        isOpen={showSubmitCountdown}
        title="Submit Assessment"
        message="You won't be able to edit this assessment after submission."
        countdownSeconds={3}
        onConfirm={() => {
          setShowSubmitCountdown(false);
          handleSubmit(onSubmit)();
        }}
        onCancel={() => setShowSubmitCountdown(false)}
      />
    </div>
  );
}
